import React from 'react';
import { TitleCard } from '@warehouse/title/ui';
import { CommonReadonly, TitleLightWithSelectedParent } from '@warehouse/title/core';
import { WorkType } from '@warehouse/title-shared/core';
import { IndexedCountryOrSubdivision, IndexedOriginalLanguage } from '@warehouse/graphql';
import { GridColDef } from '@mui/x-data-grid-pro';
import styled from 'styled-components';
import { getCustomGridSingleSelectOperators } from '../../../shared/ui/table/muix.adapter';
import { SelectAllTypography } from '../../../../src/components/library/SelectAllTypography';
import { EIDRtoTime } from '../../../../src/components/library/TimeInput';
import MultiTags from '../../../../src/components/table/columnsItems/MultiTags';
import SummaryCell from '../../../../src/views/titles/table/SummaryCell';
import { TitleCardService } from '../../ui/title-card/title-card.service';

export function IDColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'ID',
		field: 'uuid',
		editable: false,
		renderCell: (params) => (
			<SelectAllTypography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
				{params.value}
			</SelectAllTypography>
		),
		minWidth: 300,
		flex: 1,
	};
}

export function TitleCardColumnDataGrid<T extends TitleLightWithSelectedParent>(
	minWidth?: number | null,
	context: 'common' | 'relationship-tab-children' = 'common',
): GridColDef<T, T> {
	return {
		headerName: 'Title',
		field: 'titleSearchDisplayNameFull',
		editable: false,
		minWidth: minWidth || 600,
		flex: 2,
		renderCell: (params) => (
			<TitleCell className="child-table-title-cell">
				<TitleCard
					{...TitleCardService.getTitleCardPropsFromTitleCommonReadonly(params.row)}
					selectedParent={params.row?.selectedParent!}
					context={context}
					minWidth={minWidth || 600}
				/>
			</TitleCell>
		),
	};
}

interface MinimumReleaseDateColumnRow {
	releaseDate?: string | null;
}
export function ReleaseDateColumnDataGrid<T extends MinimumReleaseDateColumnRow>(): GridColDef<T, string> {
	return {
		headerName: 'Release Date',
		field: 'releaseDate',
		editable: false,
		type: 'date',
		valueFormatter: (value) => value,
		minWidth: 110,
		flex: 0.5
	};
}

export function SequenceNumberColumnDataGrid<T extends TitleLightWithSelectedParent>(): GridColDef<T, string> {
	return {
		headerName: 'Sequence Number',
		renderCell: (params) => params.row.selectedParent.sequenceInfo.number,
		cellClassName: (params) => {
			const sequenceNumber = params.row.selectedParent.sequenceInfo.number;

			return sequenceNumber === undefined ? 'sequence-number-empty' : '';
		},
		field: 'sequenceNumber',
		editable: false,
		minWidth: 140,
		flex: 0.5
	};
}

export function RunLengthColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, T> {
	return {
		headerName: 'Run Length',
		renderCell: (params) => {
			if (params.row?.workType !== WorkType.Compilation) return EIDRtoTime(params.row?.runLength);
			return '';
		},
		field: 'runLength',
		editable: false,
		minWidth: 100,
		flex: 0.5,
	};
}

export function LicensorColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Licensor',
		field: 'licensor',
		editable: false,
		renderCell: (params) => <SelectAllTypography variant="body2">{params.value}</SelectAllTypography>,
	};
}

interface MinimumReleaseDateColumnRow {
	releaseDate?: string | null;
}

export function CreatedAtColumnDataGrid<T extends { createdAt?: string | null }>(): GridColDef<T, string> {
	return {
		headerName: 'Created At',
		renderCell: (params) => params.value,
		field: 'createdAt',
		editable: false,
	};
}

export function EditedAtColumnDataGrid<T extends { editedAt?: string | null }>(): GridColDef<T, string> {
	return {
		headerName: 'Edited At',
		renderCell: (params) => params.value,
		field: 'editedAt',
		editable: false,
	};
}

export function ReleaseYearColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, number> {
	return {
		headerName: 'Release Year',
		field: 'releaseYear',
		editable: false,
		type: 'string',
		align: 'left',
		headerAlign: 'left',
		renderCell: (params) => params.value,
	};
}

export function EditedByColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Last Modified By',
		renderCell: (params) => params.value,
		field: 'editedBy',
		editable: false,
	};
}

export function CreatedByColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Created By',
		renderCell: (params) => params.value,
		field: 'createdBy',
		editable: false,
	};
}

export function RatingsColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string[]> {
	return {
		headerName: 'Ratings',
		renderCell: (params) => <MultiTags tags={params.value!} />,
		field: 'ratings',
		editable: false,
	};
}

export function AbbreviatedTitleColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Abbreviated Title',
		renderCell: (params) => params.value,
		field: 'abbreviatedTitle',
		editable: false,
	};
}

export function TitleStatusColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Status',
		renderCell: (params) => params.value,
		field: 'titleStatus',
		editable: false,
	};
}

export function Summary4000ColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Summary 4000',
		renderCell: (params) => <SummaryCell summary={params.value} />,
		field: 'summary4000',
		editable: false,
	};
}

export function Summary400ColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Summary 400',
		renderCell: (params) => <SummaryCell summary={params.value} />,
		field: 'summary400',
		editable: false,
	};
}

export function Summary190ColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Summary 190',
		renderCell: (params) => <SummaryCell summary={params.value} />,
		field: 'summary190',
		editable: false,
	};
}

export function OriginalTitleColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Original Title',
		renderCell: (params) => params.value,
		field: 'originalTitle',
		editable: false,
	};
}
export function TitleSortColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Sort Title',
		renderCell: (params) => params.value,
		field: 'titleSort',
		editable: false,
	};
}

export function TitleDisplayUnlimitedColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Display Title',
		renderCell: (params) => params.value,
		field: 'titleDisplayUnlimited',
		editable: false,
	};
}

export function FrameRateColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, number> {
	return {
		headerName: 'Frame Rate',
		renderCell: (params) => params.value,
		field: 'frameRate',
		editable: false,
	};
}

export function AspectRatioColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'Aspect Ratio',
		renderCell: (params) => params.value,
		field: 'aspectRatio',
		editable: false,
	};
}

export function EidrIdColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, string> {
	return {
		headerName: 'EIDR-S ID',
		renderCell: (params) => params.value,
		field: 'eidrId',
		editable: false,
	};
}

export function ProductionCountriesColumnDataGrid<T extends CommonReadonly>(): GridColDef<
	T,
	IndexedCountryOrSubdivision[]
> {
	return {
		headerName: 'Production Countries',
		renderCell: (params) => <MultiTags tags={params.value?.map((l) => l.label) || []} />,
		field: 'productionCountries',
		editable: false,
	};
}

export function CountriesOfOriginColumnDataGrid<T extends CommonReadonly>(): GridColDef<
	T,
	IndexedCountryOrSubdivision[]
> {
	return {
		headerName: 'Countries of Origin',
		renderCell: (params) => <MultiTags tags={params.value?.map((l) => l.label) || []} />,
		field: 'countriesOfOrigin',
		editable: false,
	};
}

export function OriginalLanguagesColumnDataGrid<T extends CommonReadonly>(): GridColDef<T, IndexedOriginalLanguage[]> {
	return {
		headerName: 'Original Languages',
		renderCell: (params) => <MultiTags tags={params.value?.map((l) => l.label) || []} />,
		field: 'originalLanguages',
		editable: false,
	};
}

export function TitleTypeColumnDataGrid<T extends CommonReadonly>(
	valueOptions: {
		value: string;
		label: string;
	}[],
): GridColDef<T, string> {
	return {
		headerName: 'Title Type',
		field: 'workType',
		editable: false,
		valueOptions,
		renderCell: (params) => params.value,
		type: 'singleSelect',
		filterOperators: getCustomGridSingleSelectOperators(),
	};
}

const TitleCell = styled.div`
	border: solid 1px transparent;
	border-radius: 9px;
	box-sizing: border-box;
	height: 100%;
	padding-left: 8px;
`;
